import type {
    IOrdinalChainProvenance,
    IOrdinalProvenanceActor,
    IOrdinalProvenanceHop,
    IOrdinalProvenanceResponse,
    IOrdinalProvenanceUser,
} from 'api/api'

export type ArtifactProvenancePresentation = {
    current: IOrdinalProvenanceActor | null
    secondary: IOrdinalProvenanceActor | null
    secondaryRole: 'creator' | 'signed by' | 'origin holder' | 'first seen' | null
    ownerCount: number
    hops: IOrdinalProvenanceHop[]
    legacyTenures: IOrdinalProvenanceResponse['ownerships']
    mintEvidence: {
        label: 'minted' | 'first seen' | 'origin block' | 'mint pending'
        timestamp: string | null
        blockHeight: number | null
    } | null
    chainStatus: IOrdinalChainProvenance['status'] | null
    chainState: IOrdinalChainProvenance['state']
    stale: boolean
}

function actorFromUser(user: IOrdinalProvenanceUser | null): IOrdinalProvenanceActor | null {
    return user ? { address: null, locking_script_hash: null, user, identity_basis: null } : null
}

export function artifactActorIdentity(actor: IOrdinalProvenanceActor | null): string | null {
    if (!actor) return null
    if (actor.address) return `address:${actor.address}`
    if (actor.locking_script_hash) return `script:${actor.locking_script_hash}`
    if (actor.user?.id) return `user:${actor.user.id}`
    return null
}

function shorten(value: string): string {
    if (value.length <= 14) return value
    return `${value.slice(0, 6)}…${value.slice(-5)}`
}

export function artifactActorDisplayName(actor: IOrdinalProvenanceActor | null): string {
    const user = actor?.user
    if (user) return user.name || user.username || user.handle || shorten(user.id)
    if (actor?.address) return shorten(actor.address)
    if (actor?.locking_script_hash) return `script ${shorten(actor.locking_script_hash)}`
    return 'unknown'
}

export function buildArtifactProvenancePresentation(
    provenance: IOrdinalProvenanceResponse | null | undefined,
): ArtifactProvenancePresentation {
    const legacyOwner = actorFromUser(provenance?.owner || null)
    const legacyGenesis = provenance?.ownerships?.find(tenure => tenure.source === 'genesis')
    const chain = provenance?.chain

    if (!chain) {
        return {
            current: legacyOwner,
            secondary: actorFromUser(legacyGenesis?.user || null),
            secondaryRole: legacyGenesis?.user
                ? provenance?.minted_by_treechat
                    ? 'creator'
                    : 'first seen'
                : null,
            ownerCount: provenance?.owner_count || 0,
            hops: [],
            legacyTenures: provenance?.ownerships || [],
            mintEvidence: legacyGenesis?.acquired_at
                ? {
                      label: provenance?.minted_by_treechat ? 'minted' : 'first seen',
                      timestamp: legacyGenesis.acquired_at,
                      blockHeight: null,
                  }
                : null,
            chainStatus: null,
            chainState: null,
            stale: false,
        }
    }

    const signer = chain.signers.find(candidate => candidate.valid) || null
    const secondary = provenance?.minted_by_treechat ? chain.creator : signer || chain.origin_holder
    const secondaryRole = secondary
        ? provenance?.minted_by_treechat
            ? 'creator'
            : signer
            ? 'signed by'
            : 'origin holder'
        : null
    const current =
        chain.state === 'terminated' || chain.state === 'indeterminate'
            ? null
            : chain.current_owner || (chain.hops.length === 0 ? legacyOwner : null)

    let mintEvidence: ArtifactProvenancePresentation['mintEvidence'] = null
    if (chain.minted_at) {
        mintEvidence = {
            label: 'minted',
            timestamp: chain.minted_at,
            blockHeight: chain.origin_block_height,
        }
    } else if (chain.origin_block_height) {
        mintEvidence = {
            label: 'origin block',
            timestamp: null,
            blockHeight: chain.origin_block_height,
        }
    } else if (chain.state === 'unconfirmed') {
        mintEvidence = { label: 'mint pending', timestamp: null, blockHeight: null }
    }

    return {
        current,
        secondary,
        secondaryRole,
        ownerCount:
            chain.holder_count || (chain.hops.length === 0 ? provenance?.owner_count || 0 : 0),
        hops: chain.hops,
        legacyTenures: provenance?.ownerships || [],
        mintEvidence,
        chainStatus: chain.status,
        chainState: chain.state,
        stale: chain.stale,
    }
}
